import React, { useContext, useEffect, useState } from 'react'
import { Col, Divider, Input, Modal, Row } from 'antd-v4'
import { Button, message, Select } from 'antd'
import { inject } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { axios } from '@getgreenline/homi-shared'
import Endpoints from '../../../../constants/Endpoints'
import { ISignageContract } from './SignagePage'
import { parseErrorMsg } from '../../../../utilities/helpers'
import { DigitalSignageContext } from './DigitalSignageContext'

const backgroundColor = '#FFFFFF'
const maxColumnItems = 100
const allowedOrientations = [0, 90, 180, 270]

interface ISignageModalProps {
  currentCompanyStore?: CurrentCompanyStore
  isModalOpen: boolean
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  isEditing: boolean
  currentSignage?: ISignageContract
}

const SignageModal: React.FC<ISignageModalProps> = inject('currentCompanyStore')(
  ({
    currentCompanyStore,
    setIsModalOpen,
    isModalOpen,
    isEditing,
    currentSignage,
  }: ISignageModalProps) => {
    const company = currentCompanyStore?.company
    const [signageName, setSignageName] = useState('')
    const [theme, setTheme] = useState('nicks-menu')
    const [primaryColor, setPrimaryColor] = useState('')
    const [secondaryColor, setSecondaryColor] = useState('')
    const [col1Name, setCol1Name] = useState('')
    const [col1Filter, setCol1Filter] = useState('')
    const [col2Name, setCol2Name] = useState('')
    const [col2Filter, setCol2Filter] = useState('')
    const [isSaving, setIsSaving] = useState(false)
    const { addSignage } = useContext(DigitalSignageContext)

    const saveSignage = async () => {
      if (!company?.id) return

      const payload = {
        name: signageName,
        tenantId: company.id.toString(),
        tenantName: company.name,
        templateLink: theme,
        primaryColor: primaryColor,
        secondaryColor: secondaryColor,
        backgroundColor: backgroundColor,
        allowedOrientations: allowedOrientations,
        sections: [
          {
            title: col1Name,
            osQuery: col1Filter,
            maxItems: maxColumnItems,
            fields: [''],
          },
          {
            title: col2Name,
            osQuery: col2Filter,
            maxItems: maxColumnItems,
            fields: [''],
          },
        ],
      }

      if (isEditing && currentSignage?.id) {
        const { data } = await axios.put(
          Endpoints.UPDATE_SIGNAGE(company.id.toString(), currentSignage.id),
          payload,
        )
        addSignage(data)
        setIsModalOpen(false)

        return
      }

      const { data } = await axios.post(Endpoints.CREATE_SIGNAGE(company.id.toString()), payload)
      addSignage(data)
      setIsModalOpen(false)
    }

    const handleSubmit = async () => {
      setIsSaving(true)
      try {
        await saveSignage()
      } catch (error) {
        message.error(parseErrorMsg(error))
      } finally {
        setIsSaving(false)
        clearFormState()
      }
    }

    const validateForm = () => {
      const formTextInputs = [
        signageName,
        primaryColor,
        secondaryColor,
        col1Name,
        col1Filter,
        col2Name,
        col2Filter,
      ]

      const emptyFieldIndex = formTextInputs.findIndex((field) => !field.length)

      if (isEditing && currentSignage) {
        const nameUpdated = currentSignage.name !== signageName
        const primaryColorUpdated = currentSignage.primaryColor !== primaryColor
        const secondaryColorUpdated = currentSignage.secondaryColor !== secondaryColor
        const col1NameUpdated = currentSignage.sections[0].title !== col1Name
        const col1FilterUpdated = currentSignage.sections[0].osQuery !== col1Filter
        const col2NameUpdated = currentSignage.sections[1].title !== col2Name
        const col2FilterUpdated = currentSignage.sections[1].osQuery !== col2Filter

        return (
          !(
            nameUpdated ||
            primaryColorUpdated ||
            secondaryColorUpdated ||
            col1NameUpdated ||
            col1FilterUpdated ||
            col2NameUpdated ||
            col2FilterUpdated
          ) || emptyFieldIndex >= 0
        )
      }

      return emptyFieldIndex >= 0
    }

    const clearFormState = () => {
      setSignageName('')
      setTheme('nicks-menu')
      setPrimaryColor('')
      setSecondaryColor('')
      setCol1Name('')
      setCol1Filter('')
      setCol2Name('')
      setCol2Filter('')
    }

    useEffect(() => {
      if (isModalOpen && isEditing && currentSignage) {
        setSignageName(currentSignage.name)
        setTheme(currentSignage.templateLink)
        setPrimaryColor(currentSignage.primaryColor)
        setSecondaryColor(currentSignage.secondaryColor)
        setCol1Name(currentSignage.sections[0].title)
        setCol1Filter(currentSignage.sections[0].osQuery)
        setCol2Name(currentSignage.sections[1].title)
        setCol2Filter(currentSignage.sections[1].osQuery)
      } else {
        clearFormState()
      }
    }, [isModalOpen, isEditing])

    return (
      <>
        <Modal
          style={{ paddingBottom: '100px' }}
          title={isEditing ? `Edit ${currentSignage?.name}` : 'Add new signage'}
          visible={isModalOpen}
          onCancel={() => {
            setIsModalOpen(false)
            clearFormState()
          }}
          footer={[
            <Button
              key='cancel'
              onClick={() => {
                setIsModalOpen(false)
                clearFormState()
              }}
            >
              Cancel
            </Button>,
            <Button
              key='create'
              type='primary'
              disabled={validateForm()}
              loading={isSaving}
              onClick={handleSubmit}
            >
              {isEditing ? 'Save' : 'Create'}
            </Button>,
          ]}
        >
          <Row gutter={12}>
            <Col md={4}>
              <label className='mt-1'>Name</label>
            </Col>
            <Col md={10}>
              <Input value={signageName} onChange={(e) => setSignageName(e.target.value)} />
            </Col>
          </Row>
          <Divider orientation='left'>Layout</Divider>
          <Row gutter={12}>
            <Col md={4}>
              <label className='mt-1'>Theme</label>
            </Col>
            <Col md={10}>
              <Select<string>
                defaultValue={theme}
                className='w-100'
                onChange={(value) => setTheme(value)}
              >
                <Select.Option value={'nicks-menu'}>Nick's Menu</Select.Option>
              </Select>
            </Col>
          </Row>
          <Row gutter={12} className='mt-2'>
            <Col md={4}>
              <label className='mt-1'>Color 1</label>
            </Col>
            <Col md={10}>
              <Input value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} />
            </Col>
          </Row>
          <Row gutter={12} className='mt-2'>
            <Col md={4}>
              <label className='mt-1'>Color 2</label>
            </Col>
            <Col md={10}>
              <Input value={secondaryColor} onChange={(e) => setSecondaryColor(e.target.value)} />
            </Col>
          </Row>
          <Divider orientation='left'>Content</Divider>
          <Row align='bottom' gutter={12}>
            <Col md={4}>
              <label className='mt-md-4 pt-md-2'>Column 1</label>
            </Col>
            <Col md={10}>
              <label>Name</label>
              <Input value={col1Name} onChange={(e) => setCol1Name(e.target.value)} />
            </Col>
            <Col md={10} className='mt-2 mt-md-0'>
              <label>Filter</label>
              <Input value={col1Filter} onChange={(e) => setCol1Filter(e.target.value)} />
            </Col>
          </Row>
          <Row gutter={12} className='mt-2'>
            <Col md={4}>
              <label className='mt-1'>Column 2</label>
            </Col>
            <Col md={10}>
              <label className='d-md-none'>Name</label>
              <Input value={col2Name} onChange={(e) => setCol2Name(e.target.value)} />
            </Col>
            <Col md={10} className='mt-2 mt-md-0'>
              <label className='d-md-none'>Filter</label>
              <Input value={col2Filter} onChange={(e) => setCol2Filter(e.target.value)} />
            </Col>
          </Row>
        </Modal>
      </>
    )
  },
)

export default SignageModal
